// Safety — Hazardous Chemicals API
// Thin service layer over the shared `api` util.

import api from './api';
import type { Chemical, ChemicalLists, SdsFile } from '../types/chemical.types';

/* -------------------------------------------------------------------------- */
/*  Chemicals CRUD                                                            */
/* -------------------------------------------------------------------------- */

export async function listChemicals(includeDeleted = false): Promise<Chemical[]> {
    const query = new URLSearchParams();
    if (includeDeleted) query.append('includeDeleted', 'true');
    const res = await api.get(`/api/safety/chemicals?${query.toString()}`);
    return res.chemicals ?? res ?? [];
}

export async function getChemical(id: string): Promise<Chemical> {
    const res = await api.get(`/api/safety/chemicals/${id}`);
    return res.chemical ?? res;
}

export async function createChemical(payload: Partial<Chemical>): Promise<Chemical> {
    const res = await api.post('/api/safety/chemicals', payload);
    return res.chemical ?? res;
}

export async function updateChemical(id: string, payload: Partial<Chemical>): Promise<Chemical> {
    const res = await api.put(`/api/safety/chemicals/${id}`, payload);
    return res.chemical ?? res;
}

/* -------------------------------------------------------------------------- */
/*  Soft delete / restore                                                     */
/* -------------------------------------------------------------------------- */

export async function softDeleteChemical(id: string, reason?: string): Promise<void> {
    await api.put(`/api/safety/chemicals/${id}/archive`, { reason });
}

export async function restoreChemical(id: string): Promise<void> {
    await api.put(`/api/safety/chemicals/${id}/restore`, {});
}

/* -------------------------------------------------------------------------- */
/*  SDS + export                                                              */
/* -------------------------------------------------------------------------- */

export async function uploadSds(id: string, file: File): Promise<SdsFile> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/api/safety/chemicals/${id}/sds`, form);
    return res.sds ?? res;
}

export async function getSdsDownloadUrl(id: string): Promise<{ downloadUrl: string; fileName: string }> {
    const res = await api.get(`/api/safety/chemicals/${id}/sds/download`);
    return res;
}

export async function deleteSds(id: string): Promise<void> {
    await api.delete(`/api/safety/chemicals/${id}/sds`);
}

export async function exportRegister(): Promise<Blob> {
    const { data } = await api.request('/api/safety/chemicals/export');
    return data;
}

/* -------------------------------------------------------------------------- */
/*  Settings (option lists)                                                   */
/* -------------------------------------------------------------------------- */

export async function getChemicalSettings(): Promise<ChemicalLists> {
    const res = await api.get('/api/safety/chemicals/settings');
    return res.settings ?? res;
}

export async function saveChemicalSettings(payload: Partial<ChemicalLists>): Promise<ChemicalLists> {
    const res = await api.put('/api/safety/chemicals/settings', payload);
    return res.settings ?? res;
}
